import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { PageHeader } from "../PageHeader";
import { EventRow } from "../shared/EventRow";
import type { Severity } from "../shared/EventRow";
import { securityRoomStore, selectDlpHitsByPattern } from "../../../stores/securityRoomStore";

// HIGH-tier detection reads the tier the SCANNER assigned (carried on the
// audit row as `dlpTier`), not a hand-copied list of pattern ids.
//
// The previous hardcoded set was a 30-entry duplicate of src/dlpPatterns.js
// that had to be updated by hand every time a pattern was added or renamed —
// a drifting allow-list deciding whether a leaked credential renders as
// "critical" or gets collapsed into a grey "medium" group. Any pattern the
// list didn't know about was silently downgraded.
function isHighTier(group: Array<Record<string, unknown>>): boolean {
  // `dlpTier` ONLY. Each fanned row carries the event's whole detail blob, so
  // an event-level `tier: "high"` rides along on every pattern — falling back
  // to it re-elevated a low-tier match that merely co-occurred with a real
  // secret, which is the exact misgrouping the per-pattern tiers removed.
  return group.some((h) => h.dlpTier === "high");
}

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

// The header shows a passive "Live" pill, not a Refresh button. The page is fed
// by a reactive Convex subscription, so there is nothing to re-fetch — and the
// old button called window.location.reload(), tearing down every live terminal
// and WebSocket in the app to "refresh" data that was already current.
export function DLPPage() {
  const snap = securityRoomStore.useStore();
  const byPattern = selectDlpHitsByPattern(snap);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Auto-expand any HIGH-tier group that is currently in the snapshot.
  // A useState lazy initializer only runs once, so HIGH-tier hits arriving
  // via SSE after mount would have stayed collapsed otherwise. We never
  // un-expand here — if the operator manually collapsed a group, leave it.
  useEffect(() => {
    setExpanded((prev) => {
      let next: Set<string> | null = null;
      for (const [id, hits] of byPattern.entries()) {
        if (isHighTier(hits) && !prev.has(id)) {
          if (!next) next = new Set(prev);
          next.add(id);
        }
      }
      return next ?? prev;
    });
  }, [byPattern]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalHits = Array.from(byPattern.values()).reduce((acc, arr) => acc + arr.length, 0);
  const groups = Array.from(byPattern.entries()).sort((a, b) => b[1].length - a[1].length);

  return (
    <>
      <PageHeader
        title="DLP"
        subtitle={`${totalHits} hits across ${groups.length} patterns`}
        actions={
          <span className="sr-live-pill" title="Updates live via Convex subscription">
            <RefreshCw size={14} aria-hidden="true" /> Live
          </span>
        }
      />
      <div className="sr-page-body">
        {groups.length === 0 && <div className="sr-empty">No DLP hits in the last 24h.</div>}
        {groups.map(([patternId, hits]) => {
          const isOpen = expanded.has(patternId);
          const isHigh = isHighTier(hits);
          const tier = isHigh ? "high" : "medium";
          const severity: Severity = isHigh ? "crit" : "ask";
          return (
            <div key={patternId} className="sr-dlp-group">
              <button
                type="button"
                className="sr-dlp-group__head"
                onClick={() => toggle(patternId)}
                aria-expanded={isOpen}
              >
                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <strong>{patternId}</strong>
                <span className="sr-dlp-group__tier">({tier})</span>
                <span className="sr-dlp-group__count">· {hits.length} hits</span>
              </button>
              {isOpen && (
                <div className="sr-dlp-group__body">
                  {hits.map((raw, i) => {
                    const h = raw as Record<string, unknown>;
                    const id = typeof h.id === "string" ? h.id : `${patternId}-${i}`;
                    const ts =
                      firstString(h, ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"]) ?? "";
                    const cmd = firstString(h, ["command", "cmd"]);
                    const agent = firstString(h, ["agent"]) ?? "";
                    const rule = firstString(h, ["rule", "ruleId"]) ?? "";
                    const who = [agent, rule].filter(Boolean).join(" · ") || undefined;
                    return (
                      <EventRow
                        key={id}
                        event={{
                          id,
                          ts,
                          severity,
                          title: cmd ?? "DLP match",
                          who,
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
